import { useState, useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/lib/supabase";

interface Preset {
  id: number;
  name: string;
  radius: number;
}

const DEFAULT_PRESETS: Preset[] = [
  { id: 1, name: "عادي", radius: 15 },
  { id: 2, name: "يوم وطني", radius: 25 },
  { id: 3, name: "رمضان", radius: 10 },
];

const STORAGE_KEY = "ghasla-coverage-presets";

export default function Coverage() {
  const { provider, loading: providerLoading } = useProvider();
  const [radius, setRadius] = useState(15);
  const [address, setAddress] = useState("حي النرجس، الرياض");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(false);

  const [presets, setPresets] = useState<Preset[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Preset[];
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore corrupted storage
    }
    return DEFAULT_PRESETS;
  });

  const [showPresetForm, setShowPresetForm] = useState(false);
  const [presetName, setPresetName] = useState("");

  const [dragging, setDragging] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (provider) {
      setRadius(provider.coverage_radius || 15);
      setAddress(provider.coverage_address || "حي النرجس، الرياض");
    }
  }, [provider]);

  const persistPresets = (next: Preset[]) => {
    setPresets(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable
    }
  };

  const handleSave = async () => {
    if (!provider) return;
    setSaving(true);
    const { error } = await supabase
      .from("providers")
      .update({ coverage_radius: radius })
      .eq("id", provider.id);
    setSaving(false);
    if (!error) {
      setToast(true);
      setTimeout(() => setToast(false), 3000);
    }
  };

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const nextId = Math.max(0, ...presets.map((p) => p.id)) + 1;
    persistPresets([...presets, { id: nextId, name, radius }]);
    setPresetName("");
    setShowPresetForm(false);
  };

  const handleDeletePreset = (id: number) => {
    if (id <= 3) return;
    persistPresets(presets.filter((p) => p.id !== id));
  };

  // Circle size mapped from radius (1-30 km) to pixels for the visual overlay
  const circleSize = Math.max(160, Math.min(460, radius * 14));

  // Drag-to-resize on the coverage circle edge
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging || !mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
    const newRadius = Math.round(dist / 7);
    setRadius(Math.max(1, Math.min(30, newRadius)));
  };

  const handlePointerUp = () => setDragging(false);

  if (providerLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4F5F5] border-t-[#00AAB7]" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">النطاق الجغرافي</h1>
      <p className="mt-1 text-sm text-ghasla-text-primary">
        العملاء داخل النطاق ده بس هم اللي يشوفوك
      </p>

      <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#FFF9EB] px-4 py-2.5 text-xs font-medium text-[#F59E0B]">
        <i className="ri-error-warning-line text-base" />
        التعديل ينعكس فوراً على ظهورك للعملاء
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[65fr_35fr]">
        {/* Map */}
        <div
          ref={mapRef}
          className="relative h-[560px] overflow-hidden rounded-[20px] bg-[#E8F5F5] shadow-card"
        >
          <iframe
            title="نطاق التغطية"
            src="https://maps.google.com/maps?q=24.7136,46.6753&z=11&output=embed"
            className={`absolute inset-0 h-full w-full border-0 ${dragging ? "pointer-events-none" : ""}`}
            loading="lazy"
          />
          {/* Coverage circle overlay */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="relative rounded-full transition-all duration-300"
              style={{ width: circleSize, height: circleSize }}
            >
              <div className="absolute inset-0 rounded-full border-2 border-[#00AAB7]/40 bg-[#00AAB7]/10" />
              {/* Center pin */}
              <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br from-[#00AAB7] to-[#33D1DA] text-white shadow-card">
                <i className="ri-map-pin-2-fill text-xl" />
              </div>
              {/* Resize handle on the edge */}
              <div
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                className="pointer-events-auto absolute left-1/2 top-0 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center rounded-full border-2 border-[#00AAB7] bg-white text-ghasla-text-link shadow-card transition-transform hover:scale-110 active:cursor-grabbing"
                title="اسحب لتغيير حجم النطاق"
              >
                <i className="ri-drag-move-2-line text-sm" />
              </div>
            </div>
          </div>
          <div className="absolute bottom-4 left-4 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-ghasla-text-primary shadow-topbar">
            {address}
          </div>
          <div className="absolute bottom-4 right-4 rounded-full bg-[#0A3D3D]/80 px-3 py-1.5 text-xs font-medium text-white shadow-topbar">
            اسحب المقبض على الحافة لتغيير النطاق
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-5">
          <div className="rounded-[20px] bg-white p-6 shadow-card">
            <p className="text-base font-semibold text-ghasla-text-heading">موقعك</p>
            <div className="mt-2 flex items-center gap-2">
              <i className="ri-map-pin-2-line text-base text-ghasla-text-secondary" />
              <p className="text-sm text-ghasla-text-primary">{address}</p>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold text-ghasla-text-heading">نطاق التغطية</p>
                <span className="text-[20px] font-bold text-ghasla-text-link">{radius} كم</span>
              </div>
              <input
                type="range"
                min={1}
                max={30}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="mt-4 w-full cursor-pointer accent-[#00AAB7]"
              />
              <div className="mt-1 flex justify-between text-xs text-ghasla-text-secondary">
                <span>1 كم</span>
                <span>30 كم</span>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-base font-semibold text-ghasla-text-heading">نطاقات محفوظة</p>
              <div className="mt-3 space-y-2">
                {presets.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-xl bg-[#F0FDFD] px-4 py-3"
                  >
                    <span className="text-sm text-ghasla-text-primary">
                      {p.name} — {p.radius} كم
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setRadius(p.radius)}
                        className="cursor-pointer whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-ghasla-text-link shadow-topbar transition-colors hover:bg-[#FAFDFD]"
                      >
                        تطبيق
                      </button>
                      {p.id > 3 && (
                        <button
                          type="button"
                          onClick={() => handleDeletePreset(p.id)}
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-ghasla-text-secondary transition-colors hover:bg-[#FFF5F5] hover:text-[#E74C3C]"
                          aria-label={`حذف ${p.name}`}
                        >
                          <i className="ri-delete-bin-6-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowPresetForm(true)}
                className="mt-3 flex cursor-pointer items-center gap-1 text-sm font-medium text-ghasla-text-link hover:underline"
              >
                <i className="ri-add-line" />
                حفظ النطاق الحالي باسم
              </button>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "جارٍ الحفظ..." : "حفظ التغييرات"}
            </button>
          </div>
        </div>
      </div>

      {/* Save preset form */}
      {showPresetForm && (
        <div className="mt-5 rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-ghasla-text-heading">حفظ النطاق الحالي</h3>
            <button
              type="button"
              onClick={() => {
                setShowPresetForm(false);
                setPresetName("");
              }}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#F0FDFD] hover:text-ghasla-text-link"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <p className="mt-2 text-sm text-ghasla-text-primary">
            سيتم حفظ النطاق الحالي <span className="font-semibold text-ghasla-text-link">{radius} كم</span> باسم جديد.
          </p>
          <div className="mt-4">
            <label htmlFor="preset-name" className="mb-1.5 block text-sm font-medium text-ghasla-text-heading">
              الاسم
            </label>
            <input
              id="preset-name"
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSavePreset();
              }}
              placeholder="مثال: عطلة نهاية الأسبوع"
              className="w-full rounded-xl border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3 text-sm text-ghasla-text-heading outline-none transition-colors placeholder:text-ghasla-text-secondary focus:border-[#00AAB7]"
            />
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              حفظ
            </button>
            <button
              type="button"
              onClick={() => {
                setShowPresetForm(false);
                setPresetName("");
              }}
              className="cursor-pointer whitespace-nowrap rounded-xl border border-[#E4EFEF] px-4 py-3 text-sm font-medium text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD]"
            >
              إلغاء
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-8 z-50 flex items-center gap-2 rounded-2xl bg-[#E8F9F0] px-5 py-4 shadow-card">
          <i className="ri-checkbox-circle-fill text-xl text-[#27AE60]" />
          <span className="text-sm font-medium text-ghasla-text-heading">
            تم تحديث النطاق ✓ — التغيير ينعكس فوراً
          </span>
        </div>
      )}
    </div>
  );
}